import { Clerk } from "@clerk/clerk-js"

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const jwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE
const clerkDomain = import.meta.env.VITE_CLERK_DOMAIN
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL
const signInUrl = import.meta.env.VITE_CLERK_SIGN_IN_URL
const signUpUrl = import.meta.env.VITE_CLERK_SIGN_UP_URL
const signInForceRedirectUrl = import.meta.env
  .VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL
const signUpForceRedirectUrl = import.meta.env
  .VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL
const signInFallbackRedirectUrl = import.meta.env
  .VITE_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL
const signUpFallbackRedirectUrl = import.meta.env
  .VITE_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL
const envAllowedRedirectOrigins = String(
  import.meta.env.VITE_CLERK_ALLOWED_REDIRECT_ORIGINS || "",
)
  .split(/[\s,]+/)
  .map((origin) => origin.trim())
  .filter(Boolean)

const resolveAllowedRedirectOrigins = () => {
  const origins = new Set(envAllowedRedirectOrigins)
  if (typeof window !== "undefined" && window.location?.origin) {
    origins.add(window.location.origin)
  }
  return Array.from(origins)
}
const legacyPasswordLogin = ["1", "true", "yes", "on"].includes(
  String(
    import.meta.env.VITE_LEGACY_PASSWORD_LOGIN_ENABLED || "",
  ).toLowerCase(),
)
const isSatellite = ["1", "true", "yes", "on"].includes(
  String(import.meta.env.VITE_CLERK_IS_SATELLITE || "").toLowerCase(),
)
const satelliteAutoSync = ["1", "true", "yes", "on"].includes(
  String(import.meta.env.VITE_CLERK_SATELLITE_AUTO_SYNC || "").toLowerCase(),
)

let clerkPromise: Promise<Clerk | null> | undefined
let jwtTemplateFallbackWarned = false

const clerkConstructorOptions = () => {
  const options: { domain?: string; proxyUrl?: string } = {}
  if (clerkDomain) options.domain = clerkDomain
  if (clerkProxyUrl) options.proxyUrl = clerkProxyUrl
  return Object.keys(options).length ? options : undefined
}

const clerkLoadOptions = () => {
  const options: Record<string, unknown> = {}
  if (isSatellite) options.isSatellite = true
  if (isSatellite) options.satelliteAutoSync = satelliteAutoSync
  if (signInUrl) options.signInUrl = signInUrl
  if (signUpUrl) options.signUpUrl = signUpUrl
  if (signInForceRedirectUrl) {
    options.signInForceRedirectUrl = signInForceRedirectUrl
  }
  if (signUpForceRedirectUrl) {
    options.signUpForceRedirectUrl = signUpForceRedirectUrl
  }
  if (signInFallbackRedirectUrl) {
    options.signInFallbackRedirectUrl = signInFallbackRedirectUrl
  }
  if (signUpFallbackRedirectUrl) {
    options.signUpFallbackRedirectUrl = signUpFallbackRedirectUrl
  }
  const resolvedAllowedRedirectOrigins = resolveAllowedRedirectOrigins()
  if (resolvedAllowedRedirectOrigins.length) {
    options.allowedRedirectOrigins = resolvedAllowedRedirectOrigins
  }
  return options
}

export const clerkAuthConfigured = () => Boolean(publishableKey)

export const legacyPasswordLoginEnabled = () =>
  !clerkAuthConfigured() || legacyPasswordLogin

export const getClerk = async () => {
  if (!publishableKey) return null
  if (!clerkPromise) {
    clerkPromise = (async () => {
      const clerk = new Clerk(publishableKey, clerkConstructorOptions())
      await clerk.load(clerkLoadOptions())
      return clerk
    })().catch((error) => {
      console.warn("Unable to load Clerk", error)
      clerkPromise = undefined
      return null
    })
  }
  return clerkPromise
}

export const subscribeToClerkSession = async (
  listener: (isSignedIn: boolean) => void,
) => {
  const clerk = await getClerk()
  if (!clerk) {
    listener(false)
    return () => {}
  }

  listener(Boolean(clerk.session))
  return clerk.addListener(({ session }) => listener(Boolean(session)), {
    skipInitialEmit: true,
  })
}

export const getClerkSessionToken = async () => {
  const clerk = await getClerk()
  if (!clerk?.session) return null

  if (jwtTemplate) {
    try {
      const token = await clerk.session.getToken({ template: jwtTemplate })
      if (token) return token
    } catch (error) {
      if (!jwtTemplateFallbackWarned) {
        console.warn(
          "Unable to get Clerk JWT template token, falling back to session token",
          error,
        )
        jwtTemplateFallbackWarned = true
      }
    }
  }

  try {
    const token = await clerk.session.getToken()
    return token || null
  } catch (error) {
    console.warn("Unable to get Clerk session token", error)
    return null
  }
}

export const signOutClerk = async () => {
  const clerk = await getClerk()
  if (!clerk?.isSignedIn) return
  await clerk.signOut()
}
